import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Calendar, Filter, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SVGMapThumbnail } from "@/components/SVGMapViewer";
import { StormTypeBadge } from "@/components/StormTypeBadge";
import { ThreatBadges } from "@/components/ThreatIndicators";
import { SnowfallBadges, SNOWFALL_TOTALS_OPTIONS } from "@/components/SnowfallInfo";
import { formatValidityPeriod } from "@/lib/utils";

const STORM_TYPES = [
  { value: "thundery_showers", label: "Thundery Showers" },
  { value: "weak", label: "Weak" },
  { value: "moderate", label: "Moderate" },
  { value: "strong", label: "Strong" },
  { value: "severe", label: "Severe" },
  { value: "violent", label: "Violent" },
];

export default function ArchivePage() {
  const [outlooks, setOutlooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState("");
  const [filterOutlookType, setFilterOutlookType] = useState("");
  const [filterStormType, setFilterStormType] = useState("");
  const [filterSnowfallTotal, setFilterSnowfallTotal] = useState("");

  // Derive available years from fetched outlooks
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchOutlooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear, filterOutlookType, filterStormType, filterSnowfallTotal]);

  const fetchOutlooks = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("outlooks")
        .select("*")
        .eq("is_published", true)
        .order("outlook_date", { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      const all = data || [];

      // Compute available years from all records (before any further filtering)
      const years = [...new Set(
        all
          .map((o) => {
            const d = o.outlook_date || o.created_at;
            return d ? new Date(d).getFullYear().toString() : null;
          })
          .filter(Boolean)
      )].sort((a, b) => b - a);
      setAvailableYears(years);

      // Client-side filtering
      let filtered = all;
      if (filterYear && filterYear !== "all") {
        filtered = filtered.filter((o) => {
          const d = o.outlook_date || o.created_at;
          return d && new Date(d).getFullYear().toString() === filterYear;
        });
      }
      if (filterOutlookType && filterOutlookType !== "all") {
        filtered = filtered.filter((o) => (o.type || "thunderstorm") === filterOutlookType);
      }
      if (filterStormType && filterStormType !== "all") {
        filtered = filtered.filter(
          (o) => Array.isArray(o.storm_types) && o.storm_types.includes(filterStormType)
        );
      }
      if (filterSnowfallTotal && filterSnowfallTotal !== "all") {
        filtered = filtered.filter(
          (o) => Array.isArray(o.snowfall_totals) && o.snowfall_totals.includes(filterSnowfallTotal)
        );
      }

      setOutlooks(filtered);
    } catch (err) {
      console.error("Failed to fetch outlooks:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterYear("");
    setFilterOutlookType("");
    setFilterStormType("");
    setFilterSnowfallTotal("");
  };

  const hasFilters =
    (filterYear && filterYear !== "all") ||
    (filterOutlookType && filterOutlookType !== "all") ||
    (filterStormType && filterStormType !== "all") ||
    (filterSnowfallTotal && filterSnowfallTotal !== "all");

  return (
    <div className="animate-fade-in" data-testid="archive-page">
      {/* Header */}
      <section className="border-b border-white/5 py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#F9FAFB] uppercase tracking-tight mb-2">
            Outlook Archive
          </h1>
          <p className="text-[#9CA3AF] max-w-2xl text-sm">
            Browse historical UK outlooks. Filter by type, year, or category.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-white/5 py-4 bg-[#111827]/50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-[#9CA3AF]">
              <Filter className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Filter</span>
            </div>

            {/* Outlook Type filter (first) */}
            <Select value={filterOutlookType} onValueChange={(v) => {
              setFilterOutlookType(v);
              setFilterStormType("");
              setFilterSnowfallTotal("");
            }}>
              <SelectTrigger
                className="w-[160px] bg-[#0B1220] border-white/10 rounded-none text-sm"
                data-testid="filter-outlook-type"
              >
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 rounded-none">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="thunderstorm">⚡ Thunderstorm</SelectItem>
                <SelectItem value="snowfall">❄ Snowfall</SelectItem>
              </SelectContent>
            </Select>

            {/* Year filter */}
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger
                className="w-[140px] bg-[#0B1220] border-white/10 rounded-none text-sm"
                data-testid="filter-year"
              >
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 rounded-none">
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Conditional secondary filter */}
            {(!filterOutlookType || filterOutlookType === "all" || filterOutlookType === "thunderstorm") && (
              <Select value={filterStormType} onValueChange={setFilterStormType}>
                <SelectTrigger
                  className="w-[180px] bg-[#0B1220] border-white/10 rounded-none text-sm"
                  data-testid="filter-storm-type"
                >
                  <SelectValue placeholder="All Storm Types" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10 rounded-none">
                  <SelectItem value="all">All Storm Types</SelectItem>
                  {STORM_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {filterOutlookType === "snowfall" && (
              <Select value={filterSnowfallTotal} onValueChange={setFilterSnowfallTotal}>
                <SelectTrigger
                  className="w-[180px] bg-[#0B1220] border-white/10 rounded-none text-sm"
                  data-testid="filter-snowfall-total"
                >
                  <SelectValue placeholder="All Snowfall Totals" />
                </SelectTrigger>
                <SelectContent className="bg-[#111827] border-white/10 rounded-none">
                  <SelectItem value="all">All Totals</SelectItem>
                  {SNOWFALL_TOTALS_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-[#9CA3AF] hover:text-[#F9FAFB] text-xs uppercase tracking-wider"
                data-testid="clear-filters-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}

            <div className="ml-auto text-xs text-[#6B7280]">
              {outlooks.length} outlook{outlooks.length !== 1 ? "s" : ""} found
            </div>
          </div>
        </div>
      </section>

      {/* Outlook List */}
      <section className="py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#111827] border border-white/10 p-4 space-y-4 animate-pulse">
                  <div className="h-28 bg-[#1e1e1e]" />
                  <div className="h-4 w-24 bg-[#1e1e1e]" />
                  <div className="h-5 w-full bg-[#1e1e1e]" />
                </div>
              ))}
            </div>
          ) : outlooks.length === 0 ? (
            <div className="text-center py-16 border border-white/10 bg-[#111827]">
              <Calendar className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
              <h3 className="font-heading text-xl text-[#F9FAFB] uppercase tracking-wide mb-2">
                No Outlooks Found
              </h3>
              <p className="text-[#9CA3AF] text-sm">
                {hasFilters
                  ? "Try adjusting your filters to find more outlooks."
                  : "No outlooks have been published yet."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {outlooks.map((outlook) => {
                const slug = outlook.outlook_date || outlook.id;
                const isSnowfall = outlook.type === "snowfall";
                const displayDate = outlook.outlook_date
                  ? new Date(outlook.outlook_date + "T00:00:00").toLocaleDateString("en-GB", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : new Date(outlook.created_at).toLocaleDateString("en-GB", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    });

                return (
                  <Link
                    key={outlook.id}
                    to={`/outlook/${slug}`}
                    className="outlook-card block bg-[#111827] border border-white/10 hover:border-[#FBBF24]/30 transition-all group"
                    data-testid={`outlook-card-${outlook.id}`}
                  >
                    {/* SVG thumbnail */}
                    <SVGMapThumbnail svgContent={outlook.svg_content} />

                    <div className="p-4 space-y-3">
                      {/* Date + type badge + active badge */}
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] text-[#9CA3AF] flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {displayDate}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {isSnowfall && (
                            <span className="text-[9px] uppercase tracking-widest font-bold text-[#A3DFFF] flex items-center gap-0.5">
                              ❄ Snow
                            </span>
                          )}
                          {outlook.is_active && (
                            <span className="text-[9px] uppercase tracking-widest font-bold text-[#22C55E] flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E]" />
                              Active
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Validity period */}
                      {formatValidityPeriod(outlook.valid_from, outlook.valid_to) && (
                        <span className="font-mono text-[10px] text-[#6B7280] flex items-center gap-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          {formatValidityPeriod(outlook.valid_from, outlook.valid_to)}
                        </span>
                      )}

                      {/* Headline */}
                      <h3 className="font-heading text-sm uppercase tracking-wide text-[#F9FAFB] group-hover:text-[#FBBF24] transition-colors line-clamp-2 leading-snug">
                        {outlook.headline || outlook.title}
                      </h3>

                      {/* Thunderstorm badges */}
                      {!isSnowfall && outlook.storm_types && outlook.storm_types.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {outlook.storm_types.map((t) => (
                            <StormTypeBadge key={t} type={t} size="small" />
                          ))}
                        </div>
                      )}
                      {!isSnowfall && <ThreatBadges outlook={outlook} />}

                      {/* Snowfall badges */}
                      {isSnowfall && <SnowfallBadges outlook={outlook} />}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
